import {
  Badge,
  Box,
  CheckIcon,
  Group,
  Loader,
  Skeleton,
  Stack,
  Table,
  Text,
} from "@mantine/core";
import { useEffect, useMemo, useState } from "react";
import CheckBoxIcon from "../../../assets/AlertCheckBox.svg";
import ActiveCheckBoxIcon from "../../../assets/AlertCheckBoxActive.svg";
import PendingIcon from "../../../assets/PendingIcon.svg";
import { DATE_FORMAT } from "../../../constants/app-constant";
import {
  gray,
  info,
  primary,
  success,
} from "../../../constants/color-constants";
import { EASTERN_TIME_ZONE } from "../../../constants/opcd-constants";
import {
  useAcknowledgeOpcdHimAlert,
  useOpcdHimAlerts,
} from "../../../hooks/queries";
import { useFhirClient } from "../../../hooks/useFhirClient";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { setOpcdHimAlertsCount } from "../../../store/providerReducer";
import type {
  OpcdHimChecklist,
  OpcdHimChecklistError,
} from "../../../types/opcd-him-alerts";
import {
  formatDate,
  formatTimeWithZoneAbbreviation,
} from "../../../utils";
import {
  getHomeScreenUserName,
  getProviderSalutation,
} from "../../../utils/provider-display-utils";
import ButtonGroup from "../../Shared/ButtonGroup";
import PollingRefreshIndicator from "../../Shared/PollingRefreshIndicator";

const OpcdHimAlerts: React.FC = () => {
  const dispatch = useAppDispatch();
  const providerNPI = useAppSelector((state) => state.provider.providerNPI);
  const provider = useAppSelector((state) => state.provider.provider);
  const practitioner = useAppSelector((state) => state.fhir.practitioner);
  const storedCount = useAppSelector(
    (state) => state.provider.opcdHimAlertsCount
  );
  const fhirClient = useFhirClient();
  const [activeStatus, setActiveStatus] = useState("Pending");
  const [acknowledgeError, setAcknowledgeError] = useState("");

  const { data, isLoading, isFetching, isError } = useOpcdHimAlerts(
    providerNPI,
    {
      enabled: Boolean(providerNPI),
      polling: true,
    }
  );
  const acknowledgeMutation = useAcknowledgeOpcdHimAlert();

  const providerName = useMemo(
    () =>
      getHomeScreenUserName(
        provider?.provider_info,
        fhirClient,
        practitioner
      ) || "-",
    [fhirClient, practitioner, provider?.provider_info]
  );
  const providerSalutation = useMemo(
    () => getProviderSalutation(provider?.provider_info, practitioner),
    [practitioner, provider?.provider_info]
  );
  const providerDisplayName = useMemo(() => {
    if (
      !providerSalutation ||
      providerName.toLowerCase().includes(providerSalutation.toLowerCase())
    ) {
      return providerName;
    }
    return /^(dr|prof)\.?$/i.test(providerSalutation)
      ? `${providerSalutation} ${providerName}`
      : `${providerName}, ${providerSalutation}`;
  }, [providerName, providerSalutation]);

  const allAlerts = useMemo<OpcdHimChecklistError[]>(
    () =>
      (data?.checklists ?? []).flatMap(
        (checklist: OpcdHimChecklist) => checklist.errors ?? []
      ),
    [data?.checklists]
  );

  const alerts = useMemo(() => {
    const sorted = [...allAlerts].sort(
      (a, b) =>
        Number(a.provider_acknowledged) - Number(b.provider_acknowledged)
    );
    if (activeStatus === "Pending") {
      return sorted.filter((alert) => !alert.provider_acknowledged);
    }
    if (activeStatus === "Acknowledged") {
      return sorted.filter((alert) => alert.provider_acknowledged);
    }
    return sorted;
  }, [activeStatus, allAlerts]);

  useEffect(() => {
    const count = allAlerts.filter(
      (alert) => !alert.provider_acknowledged
    ).length;
    if (count !== storedCount) {
      dispatch(setOpcdHimAlertsCount(count));
    }
  }, [allAlerts, dispatch, storedCount]);

  const handleAcknowledge = async (alertId: string) => {
    setAcknowledgeError("");
    if (!providerNPI) {
      setAcknowledgeError("Provider NPI is unavailable. Please try again.");
      return;
    }
    try {
      await acknowledgeMutation.mutateAsync({
        alertId,
        providerNpi: providerNPI,
      });
    } catch (error) {
      console.error("Failed to acknowledge HIM alert:", error);
      setAcknowledgeError("Unable to acknowledge the alert. Please try again.");
    }
  };

  const headerCellStyle = {
    backgroundColor: gray[2],
    padding: "6px 16px",
    borderBottom: "2px solid #dee2e6",
    fontSize: "14px",
    fontWeight: 600,
    textAlign: "left" as const,
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
    position: "sticky" as const,
    top: 0,
    zIndex: 5,
  };

  if (isLoading) {
    return (
      <Stack p="md" gap="sm">
        <Skeleton height={42} radius="md" />
        <Skeleton height={48} radius="md" />
        <Skeleton height={48} radius="md" />
      </Stack>
    );
  }

  if (isError) {
    return (
      <Box p="xl">
        <Text c="red" ta="center">
          Unable to load HIM alerts.
        </Text>
      </Box>
    );
  }

  if (!allAlerts.length) {
    return (
      <Box p="xl">
        <Text c="dimmed" ta="center">
          No HIM alerts available
        </Text>
      </Box>
    );
  }

  return (
    <Stack
      p="sm"
      gap="sm"
      style={{ height: "100%", minHeight: 0, overflow: "hidden" }}
    >
      {acknowledgeError && (
        <Text size="sm" c="red" role="alert">
          {acknowledgeError}
        </Text>
      )}

      <Group justify="flex-start" gap="sm">
        <ButtonGroup
          buttons={["Pending", "Acknowledged", "All"]}
          defaultActive="Pending"
          onChange={setActiveStatus}
        />
        <PollingRefreshIndicator visible={isFetching && !isLoading} />
      </Group>

      <Box
        style={{
          overflowX: "auto",
          overflowY: "auto",
          flex: 1,
          backgroundColor: gray[0],
        }}
      >
        <Table
          highlightOnHover
          highlightOnHoverColor={gray[0]}
          style={{
            borderCollapse: "separate",
            borderSpacing: 0,
            minWidth: 920,
          }}
        >
          <Table.Thead>
            <Table.Tr>
              <Table.Th
                style={{
                  ...headerCellStyle,
                  width: 38,
                  padding: "6px 2px",
                  textAlign: "center",
                }}
              >
                #
              </Table.Th>
              <Table.Th style={{ ...headerCellStyle, width: 120 }}>
                Provider Name
              </Table.Th>
              <Table.Th style={{ ...headerCellStyle, width: 150 }}>
                Auditor
              </Table.Th>
              <Table.Th
                style={{
                  ...headerCellStyle,
                  width: 320,
                  maxWidth: 320,
                }}
              >
                Action Required -{" "}
                <Badge
                  radius="xl"
                  size="sm"
                  variant="light"
                  color="red"
                  style={{
                    color: "#C92A45",
                    backgroundColor: "#FFE3E8",
                    border: "1px solid #F08C9D",
                    fontSize: "14px",
                    fontWeight: 700,
                    textTransform: "none",
                    verticalAlign: "middle",
                  }}
                >
                  Chart put on Hold for Billing
                </Badge>
              </Table.Th>
              <Table.Th style={{ ...headerCellStyle, width: 140 }}>
                Action/Status
              </Table.Th>
              <Table.Th style={{ ...headerCellStyle, width: 180 }}>
                Date/Time
              </Table.Th>
            </Table.Tr>
          </Table.Thead>

          <Table.Tbody>
            {!alerts.length && (
              <Table.Tr>
                <Table.Td colSpan={6} py="xl">
                  <Text size="sm" c="dimmed" ta="center">
                    No {activeStatus.toLowerCase()} HIM alerts available
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}

            {alerts.map((alert: OpcdHimChecklistError, index: number) => {
              const acknowledged = alert.provider_acknowledged;
              const isAcknowledging =
                acknowledgeMutation.isPending &&
                acknowledgeMutation.variables?.alertId === alert.id;
              const rowBackgroundColor =
                index % 2 === 0 ? "#FFFFFF" : gray[0];
              const timestamp = alert.createdAt;

              return (
                <Table.Tr
                  key={alert.id}
                  style={{ backgroundColor: rowBackgroundColor }}
                >
                  <Table.Td
                    style={{
                      width: 38,
                      padding: "10px 2px",
                      textAlign: "center",
                      verticalAlign: "middle",
                      backgroundColor: rowBackgroundColor,
                    }}
                  >
                    <Text size="sm" fw={500} c={gray[8]}>
                      {index + 1}.
                    </Text>
                  </Table.Td>

                  <Table.Td
                    style={{
                      padding: "10px 16px",
                      verticalAlign: "middle",
                      backgroundColor: rowBackgroundColor,
                    }}
                  >
                    <Text
                      size="sm"
                      fw={600}
                      c={gray[9]}
                      lineClamp={2}
                      style={{ overflowWrap: "anywhere" }}
                    >
                      {providerDisplayName}
                    </Text>
                  </Table.Td>

                  <Table.Td
                    style={{
                      padding: "10px 16px",
                      verticalAlign: "middle",
                      backgroundColor: rowBackgroundColor,
                    }}
                  >
                    <Text
                      size="sm"
                      fw={600}
                      c={gray[9]}
                      lineClamp={2}
                      style={{ overflowWrap: "anywhere" }}
                    >
                      {alert.categoryName || "-"}
                    </Text>
                  </Table.Td>

                  <Table.Td
                    style={{
                      padding: "10px 16px",
                      verticalAlign: "top",
                      backgroundColor: rowBackgroundColor,
                      width: 320,
                      maxWidth: 320,
                    }}
                  >
                    <Text size="12px" c="red" fw={600} mb="4px">
                      {alert.holdCode || "-"}
                    </Text>
                    <Text
                      size="12px"
                      lh="18px"
                      c={gray[8]}
                      style={{ overflowWrap: "anywhere" }}
                    >
                      {alert.actionRequired || "-"}
                    </Text>
                  </Table.Td>

                  <Table.Td
                    style={{
                      padding: "10px 16px",
                      verticalAlign: "middle",
                      whiteSpace: "nowrap",
                      backgroundColor: rowBackgroundColor,
                    }}
                  >
                    {acknowledged ? (
                      <Badge
                        color={success[6]}
                        variant="light"
                        radius="sm"
                        size="xs"
                        style={{
                          border: `1px solid ${success[6]}`,
                          height: "20px",
                          padding: "0 8px",
                          minWidth: "75px",
                          width: "fit-content",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          textTransform: "none",
                        }}
                      >
                        <Group gap={4} wrap="nowrap">
                          <CheckIcon
                            color={success[6]}
                            style={{ height: 20, width: 10 }}
                          />
                          <Text size="xs">Acknowledged</Text>
                        </Group>
                      </Badge>
                    ) : (
                      <Group gap="8px" wrap="nowrap">
                        <Box
                          component="img"
                          src={
                            isAcknowledging
                              ? ActiveCheckBoxIcon
                              : CheckBoxIcon
                          }
                          mb={4}
                          style={{
                            cursor: isAcknowledging ? "default" : "pointer",
                            backgroundColor: rowBackgroundColor,
                          }}
                          onClick={() => {
                            if (!isAcknowledging) {
                              void handleAcknowledge(alert.id);
                            }
                          }}
                        />
                        <Badge
                          color={info[4]}
                          variant="light"
                          radius="sm"
                          size="xs"
                          style={{
                            border: `1px solid ${info[4]}`,
                            height: "20px",
                            padding: "0 8px",
                            minWidth: "75px",
                            width: "fit-content",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            textTransform: "none",
                          }}
                        >
                          {isAcknowledging ? (
                            <Loader size={12} color={primary[5]} />
                          ) : (
                            <Group gap={4} wrap="nowrap">
                              <Box component="img" src={PendingIcon} mb={1} />
                              <Text size="xs">Pending</Text>
                            </Group>
                          )}
                        </Badge>
                      </Group>
                    )}
                  </Table.Td>

                  <Table.Td
                    style={{
                      padding: "10px 16px",
                      verticalAlign: "middle",
                      whiteSpace: "nowrap",
                      backgroundColor: rowBackgroundColor,
                    }}
                  >
                    <Text size="xs" fw={500} c={gray[8]}>
                      {formatDate(
                        timestamp,
                        DATE_FORMAT.MM_DD_YYYY,
                        EASTERN_TIME_ZONE
                      ) || "-"}
                    </Text>
                    <Text size="xs" c={gray[6]}>
                      {formatTimeWithZoneAbbreviation(
                        timestamp,
                        EASTERN_TIME_ZONE,
                        "ET"
                      )}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </Box>
    </Stack>
  );
};

export default OpcdHimAlerts;
